import { AjaxError } from "rxjs/ajax";

import { createAsyncActions } from "@ctra/utils";

import { FarmEntity } from "../farms";
import { DataDescriptorEntity } from "../data-dictionary";

import {
  ChartEntity,
  ChartFilters,
  ChartDataSourceType,
  ChartTimePeriod,
  ChartData,
  ChartDataSource,
  UnitSystemParam,
  CorrelationsResponse,
  CorrelationsIndexResponse
} from "./typings";

import types from "./types";
import { makeChartHash, normalizeChartData } from "./utils";

/**
 * Payload type for the FETCH_IFRAME_URL.pending action.
 */
export type FetchChartDataPendingPayload = {
  /**
   * auto-generated chart hash
   */
  hash: string;
  /**
   * chart id
   */
  chartID: ChartEntity["id"];
  /**
   * chart data source type
   */
  sourceType: ChartDataSourceType;
  /**
   * chart time period
   */
  timePeriod: ChartTimePeriod;
  /**
   * farm ids
   */
  farmIDs: Array<FarmEntity["id"]>;
  /**
   * Unitsystem
   */
  unitSystem: UnitSystemParam;
  /**
   * chart filters
   */
  filters?: ChartFilters;
};

/**
 * Payload type for the FETCH_IFRAME_URL.fulfilled action.
 */
export type FetchChartDataFulfilledPayload = {
  /**
   * chart id
   */
  chartID: ChartEntity["id"];
  /**
   * auto-generated chart hash
   */
  hash: string;
  /**
   * Chart data
   */
  data: ChartData;
};

/**
 * Payload type for the FETCH_IFRAME_URL.rejected action.
 */
export type FetchChartDataRejectedPayload = {
  /**
   * chart id
   */
  chartID: ChartEntity["id"];
  /**
   * auto-generated chart hash
   */
  hash: string;
  /**
   * Error string for rejection
   */
  error: AjaxError;
};

/**
 * Fetch a chart data
 * @category Action
 */
const fetchChartData = createAsyncActions(
  types.FETCH_CHART_DATA,
  (
    chartID: ChartEntity["id"],
    sourceType: ChartDataSourceType,
    timePeriod: ChartTimePeriod,
    farmIDs: Array<FarmEntity["id"]>,
    unitSystem: UnitSystemParam,
    filters?: ChartFilters
  ): FetchChartDataPendingPayload => ({
    hash: makeChartHash(chartID, timePeriod, farmIDs, unitSystem, filters),
    chartID,
    sourceType,
    timePeriod,
    farmIDs,
    unitSystem,
    filters
  }),
  (chartID: ChartEntity["id"], hash: string, response: ChartDataSource): FetchChartDataFulfilledPayload => ({
    chartID,
    hash,
    data: normalizeChartData(response)
  }),
  (chartID: ChartEntity["id"], hash: string, error: AjaxError): FetchChartDataRejectedPayload => ({
    chartID,
    hash,
    error
  })
);

/**
 * Fetch farm correlations
 * @category Action
 */
const fetchFarmCorrelations = createAsyncActions(
  types.FETCH_FARM_CORRELATIONS,
  (farmID: FarmEntity["id"]) => ({
    farmID
  }),
  (farmID: FarmEntity["id"], response: CorrelationsIndexResponse) => ({
    farmID,
    intervals: response.intervalDays
  }),
  (farmID: FarmEntity["id"], error: AjaxError) => ({ farmID, error })
);

/**
 * Fetch chart correlations
 * @category Action
 */
const fetchChartCorrelations = createAsyncActions(
  types.FETCH_CHART_CORRELATIONS,
  (
    farmID: FarmEntity["id"],
    dataDescriptorID: DataDescriptorEntity["id"],
    excludedDataDescriptorIDList?: DataDescriptorEntity["id"][]
  ) => ({
    farmID,
    dataDescriptorID,
    excludedDataDescriptorIDList
  }),
  (farmID: FarmEntity["id"], response: CorrelationsResponse) => ({
    farmID,
    intervals: response.intervalDays
  }),
  (farmID: FarmEntity["id"], error) => ({ farmID, error })
);

export default {
  fetchChartData,
  fetchFarmCorrelations,
  fetchChartCorrelations
};
